// src/services/track.js
import { doc, serverTimestamp, setDoc, Timestamp } from "firebase/firestore";
import { DateTime } from "luxon";
import { db } from "../firebase";
import { safeLocalStorage } from "../utils/safeStorage";

const STORAGE_KEY = "events:v1";
const MAX_ONCE_ENTRIES = 1500;
const EVENT_RETENTION_MS = 90 * 24 * 60 * 60 * 1000;

const safeParse = (s) => {
  try {
    return JSON.parse(s);
  } catch (e) {
    return null;
  }
};

const getOnceMap = () => {
  const raw = safeLocalStorage.getItem(STORAGE_KEY);
  const parsed = raw ? safeParse(raw) : null;
  return parsed && typeof parsed === "object" ? parsed : {};
};

const setOnceMap = (obj) => {
  safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
};

const pruneOnceMapIfNeeded = (mapObj) => {
  const keys = Object.keys(mapObj || {});
  if (keys.length <= MAX_ONCE_ENTRIES) return mapObj;

  // prune oldest first (values are timestamps)
  keys.sort((a, b) => (mapObj[a] || 0) - (mapObj[b] || 0));
  const toDelete = keys.length - MAX_ONCE_ENTRIES;
  for (let i = 0; i < toDelete; i++) delete mapObj[keys[i]];
  return mapObj;
};

const toBase64Url = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let str = "";
  for (let i = 0; i < bytes.length; i++) str += String.fromCharCode(bytes[i]);
  return btoa(str).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/g, "");
};

const sha256Base64Url = async (input) => {
  // Browser native hashing
  const enc = new TextEncoder();
  const data = enc.encode(input);
  const hash = await crypto.subtle.digest("SHA-256", data);
  return toBase64Url(hash);
};

const dayKeyLA = () =>
  DateTime.now().setZone("America/Los_Angeles").toISODate();

const isLocalAnalyticsMode = () => {
  if (process.env.NODE_ENV !== "production") return true;
  if (process.env.REACT_APP_FIREBASE_ENV === "development") return true;

  if (typeof window !== "undefined") {
    const host = String(window.location?.hostname || "").toLowerCase();
    if (host === "localhost" || host === "127.0.0.1") return true;
  }

  return false;
};

export const buildIdempotencyKey = ({
  scope = "session", // "session" | "user" | "day"
  event,
  userId = null,
  sessionId = null,
  key = null, // optional extra disambiguator (e.g. path, step name)
}) => {
  const subject =
    scope === "user"
      ? userId || "anon"
      : scope === "day"
      ? userId || sessionId || "anon"
      : sessionId || userId || "anon";

  const day = scope === "day" ? dayKeyLA() : "";
  const extra = key ? String(key) : "";

  // Human-readable raw key (we hash it for doc id)
  return [scope, event, subject, day, extra].filter(Boolean).join("|");
};

export const trackIdempotent = async ({
  event,
  userId = null,
  sessionId = null,
  properties = {},
  context = {},
  idempotencyKey, // REQUIRED for idempotent writes
}) => {
  if (!event || !idempotencyKey) return null;
  if (isLocalAnalyticsMode()) return null;

  try {
    const eventId = await sha256Base64Url(idempotencyKey);

    const payload = {
      event,
      userId: userId || null,
      sessionId: sessionId || null,

      step:
        properties?.step != null && properties?.step !== ""
          ? properties.step
          : properties?.view != null && properties?.view !== ""
          ? properties.view
          : null,

      properties: properties || {},
      context: context || {},
      idempotencyKey, // store for debugging
      createdAt: Timestamp.now(),
      createdAtServer: serverTimestamp(),
      expiresAt: Timestamp.fromMillis(Date.now() + EVENT_RETENTION_MS),
    };

    // Deterministic doc id
    // If doc already exists, this becomes an UPDATE and will be rejected by rules
    await setDoc(doc(db, "events", eventId), payload);

    return eventId;
  } catch (err) {
    // If rules reject updates, Firestore throws. That’s expected for duplicates.
    // We silently ignore duplicates.
    const msg = String(err?.message || "");
    const code = err?.code || "";
    if (
      code === "permission-denied" ||
      msg.toLowerCase().includes("permission")
    ) {
      return null;
    }
    console.error("trackIdempotent failed:", err);
    return null;
  }
};

export const trackOnce = async ({
  event,
  userId = null,
  sessionId = null,
  scope = "session",
  key = null,
  properties = {},
  context = {},
}) => {
  if (!event) return null;

  const idemKey = buildIdempotencyKey({
    scope,
    event,
    userId,
    sessionId,
    key,
  });

  // client-side once guard (single localStorage map)
  try {
    const onceMap = getOnceMap();
    const now = Date.now();

    // store by idempotency key (human-readable) OR by hash (smaller)
    // If you prefer smaller storage, swap idemKey -> (await sha256Base64Url(idemKey))
    if (onceMap[idemKey]) return null;

    onceMap[idemKey] = now;
    setOnceMap(pruneOnceMapIfNeeded(onceMap));
  } catch (e) {
    // If storage fails (Safari private mode etc), still rely on Firestore idempotency
  }

  const res = await trackIdempotent({
    event,
    userId,
    sessionId,
    properties,
    context,
    idempotencyKey: idemKey,
  });

  return res;
};
